/**
 * Remote-control state for the browser — a port of the mobile app's store.
 * The relay client feeds snapshots + events in; the /remote page reads
 * merged, sidecar-tagged views out.
 */
import { create } from 'zustand';
import { listDevices, revokeDevice } from './api';
import { fingerprintWords } from './crypto';
import { clearIdentity, ensureIdentity, loadIdentity, relay, type DeviceIdentity } from './relay';
import type {
  AttentionEntry,
  ChatItem,
  PermissionRequest,
  ProjectMeta,
  RemoteDevice,
  SessionRow,
  SessionStatus,
  Snapshot,
  WorktreeMeta,
} from './protocol';

export interface SidecarState {
  deviceId: string;
  name: string;
  handshaken: boolean;
  projects: ProjectMeta[];
  worktrees: WorktreeMeta[];
  sessions: SessionRow[];
  attention: AttentionEntry[];
  lastSnapshotAt: number;
}

export interface TranscriptView {
  gen: number;
  seq: number;
  items: ChatItem[];
  status: SessionStatus;
  pendingPermissions: PermissionRequest[];
  streamText: string;
  streamThinking: string;
  queue: { id: string; text: string }[];
  contextTokens?: number;
  contextWindow?: number;
  loaded: boolean;
}

const emptyTranscript = (): TranscriptView => ({
  gen: 0,
  seq: 0,
  items: [],
  status: 'idle',
  pendingPermissions: [],
  streamText: '',
  streamThinking: '',
  queue: [],
  loaded: false,
});

function browserName(): string {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Safari\//.test(ua)
        ? 'Safari'
        : /Firefox\//.test(ua)
          ? 'Firefox'
          : 'Browser';
  const os = /Mac/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : '';

  return os ? `${browser} on ${os}` : browser;
}

interface RemoteState {
  started: boolean;
  identity: DeviceIdentity | null;
  deviceState: string;
  fingerprint: string | null;
  devices: RemoteDevice[];
  relayConnected: boolean;
  sidecars: Record<string, SidecarState>;
  sessionOwner: Record<string, string>;
  transcripts: Record<string, TranscriptView>;

  init(): Promise<void>;
  pair(): Promise<void>;
  unpair(): Promise<void>;
  refreshDevices(): Promise<void>;

  loadTranscript(sessionId: string): Promise<void>;
  sendMessage(sessionId: string, text: string): Promise<void>;
  respondPermission(sessionId: string, requestId: string, approved: boolean, message?: string): Promise<void>;
  interrupt(sessionId: string): Promise<void>;
  markRead(id: string, ts: number): void;
  createTask(sidecarId: string, projectId: string, task: string): Promise<void>;
  merge(worktreeId: string): Promise<{ status: string; url?: string; message?: string }>;
  rpcForSession<T = unknown>(sessionId: string, method: string, params?: object): Promise<T>;
  rpcFor<T = unknown>(sidecarId: string, method: string, params?: object): Promise<T>;
}

let relayWired = false;

export const useRemote = create<RemoteState>((set, get) => {
  const patchTranscript = (
    sessionId: string,
    patch: Partial<TranscriptView> | ((t: TranscriptView) => TranscriptView),
  ) => {
    set((s) => {
      const cur = s.transcripts[sessionId] ?? emptyTranscript();
      const next = typeof patch === 'function' ? patch(cur) : { ...cur, ...patch };

      return { transcripts: { ...s.transcripts, [sessionId]: next } };
    });
  };

  const wireRelay = () => {
    if (relayWired) {
      return;
    }

    relayWired = true;

    relay.on('status', (st) => {
      set((s) => {
        const sidecars = { ...s.sidecars };

        for (const id of Object.keys(sidecars)) {
          sidecars[id] = { ...sidecars[id], handshaken: st.handshaken.includes(id) };
        }

        return { relayConnected: st.connected, sidecars };
      });
    });

    relay.on('devices', (devices) => {
      const { identity } = get();
      const mine = identity ? devices.find((d) => d.id === identity.deviceId) : undefined;
      set({ devices, deviceState: mine?.state ?? get().deviceState });
    });

    relay.on('snapshot', (sidecarId, snap: Snapshot) => {
      set((s) => {
        const owner = { ...s.sessionOwner };

        for (const row of snap.sessions ?? []) {
          owner[row.id] = sidecarId;
        }

        return {
          sessionOwner: owner,
          sidecars: {
            ...s.sidecars,
            [sidecarId]: {
              deviceId: sidecarId,
              name: relay.sidecarName(sidecarId),
              handshaken: true,
              projects: snap.projects ?? [],
              worktrees: snap.worktrees ?? [],
              sessions: snap.sessions ?? [],
              attention: snap.attention ?? [],
              lastSnapshotAt: Date.now(),
            },
          },
        };
      });

      // reconcile any open transcript against the fresh world
      for (const sessionId of Object.keys(get().transcripts)) {
        if (get().sessionOwner[sessionId] === sidecarId && get().transcripts[sessionId].loaded) {
          void get().loadTranscript(sessionId);
        }
      }
    });

    relay.on('stream', (_sidecarId, d) => {
      patchTranscript(d.sessionId, (t) => ({
        ...t,
        streamText: t.streamText + (d.text ?? ''),
        streamThinking: t.streamThinking + (d.thinking ?? ''),
      }));
    });

    relay.on('event', (sidecarId, event, data) => {
      const s = get();
      const sc = s.sidecars[sidecarId];

      switch (event) {
        case 'session.status': {
          const d = data as { sessionId: string; status: SessionStatus };

          if (sc) {
            set({
              sidecars: {
                ...s.sidecars,
                [sidecarId]: {
                  ...sc,
                  sessions: sc.sessions.map((row) => (row.id === d.sessionId ? { ...row, status: d.status } : row)),
                },
              },
            });
          }

          if (s.transcripts[d.sessionId]) {
            patchTranscript(d.sessionId, { status: d.status });
          }

          return;
        }
        case 'session.item': {
          const d = data as { sessionId: string; item: ChatItem; gen: number };
          patchTranscript(d.sessionId, (t) => {
            if (!t.loaded) {
              return t;
            }

            if (d.gen !== t.gen) {
              void get().loadTranscript(d.sessionId);
              return t;
            }

            if (t.items.some((it) => it.id === d.item.id)) {
              return t;
            }

            return {
              ...t,
              items: [...t.items, d.item],
              seq: d.item.seq ?? t.seq,
              streamText: '',
              streamThinking: '',
            };
          });

          return;
        }
        case 'session.item_updated': {
          const d = data as { sessionId: string; item: ChatItem };
          patchTranscript(d.sessionId, (t) =>
            t.loaded ? { ...t, items: t.items.map((it) => (it.id === d.item.id ? d.item : it)) } : t,
          );

          return;
        }
        case 'session.permission_request': {
          const d = data as PermissionRequest;
          patchTranscript(d.sessionId, (t) => ({
            ...t,
            pendingPermissions: t.pendingPermissions.some((p) => p.requestId === d.requestId)
              ? t.pendingPermissions
              : [...t.pendingPermissions, d],
          }));

          return;
        }
        case 'session.permission_resolved': {
          const d = data as { sessionId: string; requestId: string };
          patchTranscript(d.sessionId, (t) => ({
            ...t,
            pendingPermissions: t.pendingPermissions.filter((p) => p.requestId !== d.requestId),
          }));

          return;
        }
        case 'session.queue': {
          const d = data as { sessionId: string; items: { id: string; text: string }[] };
          patchTranscript(d.sessionId, (t) => ({ ...t, queue: d.items }));

          return;
        }
        case 'session.context': {
          const d = data as { sessionId: string; tokens: number; window: number };
          patchTranscript(d.sessionId, (t) => ({ ...t, contextTokens: d.tokens, contextWindow: d.window }));

          return;
        }
        case 'sessions.changed': {
          const d = data as { sessions: SessionRow[] };

          if (!sc) {
            return;
          }

          const owner = { ...s.sessionOwner };

          for (const row of d.sessions) {
            owner[row.id] = sidecarId;
          }
          set({ sessionOwner: owner, sidecars: { ...s.sidecars, [sidecarId]: { ...sc, sessions: d.sessions } } });

          return;
        }
        case 'worktrees.changed': {
          const d = data as { projectId: string; worktrees: WorktreeMeta[] };

          if (!sc) {
            return;
          }

          const others = sc.worktrees.filter((w) => w.projectId !== d.projectId);
          set({ sidecars: { ...s.sidecars, [sidecarId]: { ...sc, worktrees: [...others, ...d.worktrees] } } });

          return;
        }
        case 'attention.changed': {
          const d = data as { entries: AttentionEntry[] };

          if (!sc) {
            return;
          }

          set({ sidecars: { ...s.sidecars, [sidecarId]: { ...sc, attention: d.entries } } });

          return;
        }
        default:
          return;
      }
    });
  };

  return {
    started: false,
    identity: null,
    deviceState: 'unknown',
    fingerprint: null,
    devices: [],
    relayConnected: false,
    sidecars: {},
    sessionOwner: {},
    transcripts: {},

    async init() {
      if (get().started) {
        return;
      }

      const identity = loadIdentity();
      set({
        started: true,
        identity,
        fingerprint: identity ? fingerprintWords(identity.publicKey) : null,
      });

      if (identity) {
        await get()
          .refreshDevices()
          .catch(() => undefined);
        wireRelay();
        await relay.start(identity);
      }
    },

    async pair() {
      const identity = await ensureIdentity(browserName());
      set({ identity, fingerprint: fingerprintWords(identity.publicKey) });
      await get().refreshDevices();
      wireRelay();
      await relay.start(identity);
    },

    async unpair() {
      relay.stop();

      const { identity } = get();

      if (identity) {
        try {
          await revokeDevice(identity.deviceId);
        } catch {
          // best-effort
        }
      }

      clearIdentity();
      set({
        identity: null,
        deviceState: 'unknown',
        fingerprint: null,
        devices: [],
        sidecars: {},
        sessionOwner: {},
        transcripts: {},
        relayConnected: false,
      });
    },

    async refreshDevices() {
      const devices = await listDevices();
      const { identity } = get();
      const mine = identity ? devices.find((d) => d.id === identity.deviceId) : undefined;
      set({ devices, deviceState: mine?.state ?? 'unknown' });
    },

    async loadTranscript(sessionId) {
      const res = await get().rpcForSession<{
        gen: number;
        seq: number;
        items: ChatItem[];
        status: SessionStatus;
        pendingPermissions: PermissionRequest[];
      }>(sessionId, 'session.transcript', { sessionId, limit: 200, truncate: true });
      patchTranscript(sessionId, (t) => ({
        ...t,
        gen: res.gen,
        seq: res.seq,
        items: res.items,
        status: res.status,
        pendingPermissions: res.pendingPermissions ?? [],
        streamText: '',
        streamThinking: '',
        loaded: true,
      }));
    },

    async sendMessage(sessionId, text) {
      await get().rpcForSession(sessionId, 'session.send', { sessionId, text });
    },

    async respondPermission(sessionId, requestId, approved, message) {
      await get().rpcForSession(sessionId, 'session.permissionResponse', { requestId, approved, message });
      patchTranscript(sessionId, (t) => ({
        ...t,
        pendingPermissions: t.pendingPermissions.filter((p) => p.requestId !== requestId),
      }));
    },

    async interrupt(sessionId) {
      await get().rpcForSession(sessionId, 'session.interrupt', { sessionId });
    },

    markRead(id, ts) {
      const { sidecars } = get();

      for (const sc of Object.values(sidecars)) {
        const hit = sc.attention.some(
          (e) => (e.kind === 'session' && e.sessionId === id) || (e.kind === 'pr' && e.worktreeId === id),
        );

        if (hit && sc.handshaken) {
          void relay.rpc(sc.deviceId, 'remote.markRead', { id, ts }).catch(() => undefined);
        }
      }
    },

    async createTask(sidecarId, projectId, task) {
      await relay.rpc(sidecarId, 'worktree.createAuto', { projectId, task }, 60_000);
    },

    async merge(worktreeId) {
      const sc = Object.values(get().sidecars).find((x) => x.worktrees.some((w) => w.id === worktreeId));

      if (!sc) {
        throw new Error('No Mac owns this worktree');
      }

      return relay.rpc(sc.deviceId, 'pr.merge', { worktreeId }, 120_000);
    },

    rpcForSession(sessionId, method, params = {}) {
      const sidecarId = get().sessionOwner[sessionId];

      if (!sidecarId) {
        return Promise.reject(new Error('No Mac owns this session'));
      }

      return relay.rpc(sidecarId, method, params);
    },

    rpcFor(sidecarId, method, params = {}) {
      return relay.rpc(sidecarId, method, params);
    },
  };
});

/** Attention across Macs, worst first. */
export function allAttention(s: Pick<RemoteState, 'sidecars'>): (AttentionEntry & { sidecarId: string })[] {
  const rank = (e: AttentionEntry) => (e.kind === 'pr' ? 3 : ({ permission: 0, error: 1, done: 2 } as const)[e.reason]);

  return Object.values(s.sidecars)
    .flatMap((sc) => sc.attention.map((e) => ({ ...e, sidecarId: sc.deviceId })))
    .sort((a, b) => rank(a) - rank(b) || b.ts - a.ts);
}
